/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {CustomButtonCellComponent} from '../../../core/components/custom-button-cell/custom-button-cell.component';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {MarketAreaPlanningMarketMap} from '../../../models/market-area-planning-market-map.model';
import {PlanningMarket} from '../../../models/planning-market.model';
import {Region} from '../../../models/region';
import {MarketAreaReport} from '../../../models/reports/market-area-report';
import {ExcelExportService} from '../../../services/excel-export.service';
import {MarketAreaService} from '../../../services/market-area.service';
import {PlanningMarketService} from '../../../services/planning-market.service';
import {RegionService} from '../../../services/region.service';
import {AddMarketAreaPopUpComponent} from './add-market-area-pop-up/add-market-area-pop-up.component';
import {EditMarketAreaPopUpComponent} from './edit-market-area-pop-up/edit-market-area-pop-up.component';

@Component({
    selector: 'app-manage-market-area-grouping',
    templateUrl: './manage-market-area-grouping.component.html',
    styleUrls: ['./manage-market-area-grouping.component.scss'],
    standalone: false
})
export class ManageMarketAreaGroupingComponent implements OnInit {
  constructor(public snackBar: FabMessageService,
              private readonly marketAreaService: MarketAreaService,
              private readonly planningMarketService: PlanningMarketService,
              public readonly excelService: ExcelExportService,
              public readonly regionService: RegionService,
              public dialog: MatDialog) {
  }

  showSpinner: boolean;
  pageSize: number;

  worksheetExportRequest: WorksheetExportRequest;
  marketAreaReport: MarketAreaReport[];
  marketAreaReportHeaders: ColumnHeader[] = [
    {name: 'region', label: 'REGION'},
    {name: 'subRegion', label: 'SUBREGION'},
    {name: 'mktArea', label: 'MKTAREA'},
    {name: 'subMktArea', label: 'SUBMKTAREA'},
    {name: 'plngMkt', label: 'PLNGMKT'},
    {name: 'active', label: 'ACTIVE'}
  ];
  marketAreaHeaders: ColumnHeader[] = [
    {name: 'mktArea', label: 'MKTAREA'}
  ];

  columnDefs: ColDef[] = [
    {headerName: 'MKTAREA', field: 'mktArea', filter: AutocompleteFilterComponent},
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: CustomButtonCellComponent,
      cellRendererParams: {
        icon: 'edit',
        onClick: this.editMarketAreaPlanningMarket.bind(this)
      }
    }
  ];

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };


  MARKET_AREA_REPORT = 'Market Area Report';

  selectedRegion: Region;
  regions: Region[];
  selectedMktArea: string;
  mktAreas: { mktArea: string }[];
  planningMarketsInMarketArea: MarketAreaPlanningMarketMap[];
  regionsPlanningMarketList: PlanningMarket[];


  ngOnInit(): void {
    this.pageSize = 10;
    this.showSpinner = false;
    this.selectedMktArea = '';
    this.getMarketAreaReport();
    this.getRegions();
  }

  /**
   * Gets a list of regions
   */
  getRegions(): void {
    this.regionService.getAllRegions().subscribe({
      next: (data: Region[]) => {
        this.regions = data;
      },
      error: (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    });
  }

  /**
   * Based on the region selected, grabs the list of market areas and planning markets in the region
   * @param region - Region
   */
  getPlngMktAndMktArea(region: Region): void {
    this.selectedMktArea = '';
    this.getMarketAreas(region);
    this.getPlanningMarkets(region);
  }

  /**
   * Sends a get request to get a list of market areas in the region
   * @param region - Region
   */
  getMarketAreas(region: Region): void {
    this.marketAreaService.getMarketAreasByRegion(region.region).subscribe({
      next: (data: string[]) => {
        this.mktAreas = [];
        for (const area of data) {
          this.mktAreas.push({mktArea: area});
        }
      },
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.selectedMktArea = '';
      }
    });
  }

  /**
   * Sends a get request to get a list of planning markets in the region
   * @param region - Region
   */
  getPlanningMarkets(region: Region): void {
    this.planningMarketService.getPlanningMarketsByRegion(region.region).subscribe({
      next: (data: PlanningMarket[]) => {
        this.regionsPlanningMarketList = data;
      },
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    });
  }

  /**
   * Called when the edit button of a market area is clicked.
   * Gets a list of Market Area Planning Markets mapping records based on Market Area.
   * Then calls function to open edit pop up.
   * @param row the row data of the clicked icon
   */
  editMarketAreaPlanningMarket(row: any): void {
    const mktArea = row.mktArea;
    this.planningMarketService.getPlanningMarketMappingsByMarketArea(mktArea).subscribe({
      next: (data: MarketAreaPlanningMarketMap[]) => {
        this.planningMarketsInMarketArea = data;
      },
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.openEditMarketAreaDialog(mktArea);
      }
    });
  }

  /**
   * Opens the edit market area pop up and returns the list of the updated planning markets in the market area.
   * Those planning markets get mapped to an MarketAreaPlanningMarketMap list with the market area then
   * sent to the backend to be stored, by first deleting the current Market Area Planning Market Mappings
   * and then adding the new mappings.
   * @param mktArea - string
   */
  openEditMarketAreaDialog(mktArea: string): void {
    const dialogRef = this.dialog.open(EditMarketAreaPopUpComponent, {
      data: {
        title: `Edit Market Area:`,
        mktArea,
        regionsPlanningMarketList: this.regionsPlanningMarketList,
        marketAreasPlanningMarketList: this.planningMarketsInMarketArea
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.showSpinner = true;
        const marketAreaPlanningMarketMaps: MarketAreaPlanningMarketMap[] = [];
        const selectedPlanningMarketMaps: string[] = res.data.selectedPlanningMarkets;
        selectedPlanningMarketMaps.forEach((element: string) => {
          marketAreaPlanningMarketMaps.push({mktArea, plngMkt: element, subMktArea: ''});
        });
        this.marketAreaService.deletePlanningMarketMappingByMarketArea(mktArea).subscribe({
          error: err => {
            this.showSpinner = false;
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.marketAreaService.addPlanningMarketMapping(marketAreaPlanningMarketMaps).subscribe({
              error: err => {
                this.showSpinner = false;
                this.snackBar.displayError(err, DISMISS_MSG);
              },
              complete: () => {
                this.snackBar.displaySuccess(`Successfully edited Market Area: ${mktArea}.`, DISMISS_MSG);
                this.getMarketAreas(this.selectedRegion);
                this.getMarketAreaReport();
              }
            });
          }
        });
      }
    });
  }

  /**
   * Opens the add market area pop up and return the new market area and
   * the list of the updated planning markets in the market area.
   * Those planning markets get mapped to an MarketAreaPlanningMarketMap list with the market area then
   * sent to the backend to be stored, by first adding the market area to the market area table
   * and then adding the new mappings.
   */
  openAddMktArea(): void {
    const dialogRef = this.dialog.open(AddMarketAreaPopUpComponent, {
      data: {
        title: 'Add New Market Area',
        regionsPlanningMarketList: this.regionsPlanningMarketList
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.showSpinner = true;
        const marketAreaPlanningMarketMaps: MarketAreaPlanningMarketMap[] = [];
        const selectedPlanningMarketMaps: string[] = res.data.selectedPlanningMarkets;
        selectedPlanningMarketMaps.forEach((element: string) => {
          marketAreaPlanningMarketMaps.push({mktArea: res.data.newValue, plngMkt: element, subMktArea: ''});
        });
        this.marketAreaService.addMarketArea(res.data.newValue).subscribe({
          error: err => {
            this.showSpinner = false;
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.marketAreaService.addPlanningMarketMapping(marketAreaPlanningMarketMaps).subscribe({
              error: err => {
                this.showSpinner = false;
                this.snackBar.displayError(err, DISMISS_MSG);
              },
              complete: () => {
                this.snackBar.displaySuccess(`Successfully added Market Area: ${res.data.newValue}.`, DISMISS_MSG);
                this.getMarketAreas(this.selectedRegion);
                this.getMarketAreaReport();
              }
            });
          }
        });
      }
    });
  }

  /**
   * Opens the delete market area pop up and returns a result of true if market area is to be deleted.
   * Those planning market mappings are then deleted and then the market area record is deleted.
   */
  openDeleteMktArea(): void {
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: 'Delete',
        valueToDelete: this.selectedMktArea
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res && res.data.deleteValue) {
        this.showSpinner = true;
        this.marketAreaService.deletePlanningMarketMappingByMarketArea(this.selectedMktArea).subscribe({
          error: err => {
            this.showSpinner = false;
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.marketAreaService.deleteMarketArea(this.selectedMktArea).subscribe({
              error: err => {
                this.showSpinner = false;
                this.snackBar.displayError(err, DISMISS_MSG);
              },
              complete: () => {
                this.snackBar.displaySuccess(`Successfully Deleted ${this.selectedMktArea}.`, DISMISS_MSG);
                this.getMarketAreas(this.selectedRegion);
                this.getMarketAreaReport();
              }
            });
          }
        });
      }
    });
  }

  /**
   * Gets a list of market area report records to be exported to an excel file
   */
  getMarketAreaReport(): void {
    this.marketAreaService.getMarketAreaReport().subscribe({
      next: (data: MarketAreaReport[]) => {
        this.marketAreaReport = data;
      },
      error: (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.setExportRequest();
      }
    });
  }

  /**
   * Helper function used to set the export request
   */
  setExportRequest(): void {
    const excelData = new Map<string, MarketAreaReport[]>();
    const numTypeFields = new Map<string, string[]>();
    numTypeFields[this.MARKET_AREA_REPORT] = ['active'];
    excelData[this.MARKET_AREA_REPORT] = this.marketAreaReport;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');

    this.worksheetExportRequest = this.excelService.createExportRequestBody('MarketAreas_' + currDate,
      excelData,
      [[this.MARKET_AREA_REPORT, this.marketAreaReportHeaders]],
      numTypeFields);
    this.showSpinner = false;
  }


  /**
   * on select of a market area
   * @param event The event
   */
  onSelect(event: any) {
    const row = event.data;
    if (event.node.selected) {
      this.selectedMktArea = row.mktArea;
    } else if (row.mktArea === this.selectedMktArea) {
      this.selectedMktArea = null;
    }
  }
}
